import {
    BillingPeriodId,
    BillingPeriodIdMap,
    CommitmentPeriodId,
    defaultBillingPeriodId,
    defaultCommitmentPeriodId,
    defaultCommitmentPeriods,
    defaultMembershipTierId,
} from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { addMonths, format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { tenYearsEndMonth } from "../../../date-time-picker/DateTimePicker";
import { useMemberProfile } from "../../MemberProfileContext";
import { DateField, OwnedField } from "../FormFields";
import { FormShell } from "../ProfileOverlayShell";
import type { FormProps } from "../types";

const upgradeMembershipSchema = z.object({
    tier: z.string(),
    commitmentPeriod: z.string(),
    billingPeriod: z.string(),
});

type UpgradeFormValues = z.infer<typeof upgradeMembershipSchema>;

export function UpgradeMembershipForm({ onClose }: FormProps) {
    const { profile, membershipTiers, upgradeMembership } = useMemberProfile();
    const [startDate, setStartDate] = useState(new Date());
    const { control, handleSubmit, watch } = useForm<UpgradeFormValues>({
        resolver: zodResolver(upgradeMembershipSchema),
        defaultValues: {
            tier: profile.membership.tier ?? defaultMembershipTierId,
            commitmentPeriod: defaultCommitmentPeriodId,
            billingPeriod: defaultBillingPeriodId,
        },
    });
    const commitmentPeriod = watch("commitmentPeriod");
    const renewalDate = addMonths(
        startDate,
        defaultCommitmentPeriods.find((period) => period.id === commitmentPeriod)
            ?.value ?? 0,
    );

    return (
        <FormShell
            title="Upgrade to paid membership"
            onClose={onClose}
            saveLabel="Upgrade"
            saveDisabled={!startDate}
            onSave={handleSubmit((values) => {
                if (!startDate) return;
                upgradeMembership(
                    {
                        tier: values.tier,
                        commitmentPeriod: CommitmentPeriodId.parse(
                            values.commitmentPeriod,
                        ),
                        billingPeriod: BillingPeriodId.parse(values.billingPeriod),
                        startDate: format(startDate, "yyyy-MM-dd"),
                    },
                    onClose,
                );
            })}
        >
            <OwnedField
                id="upgrade-tier"
                label="Tier"
                kind="select"
                options={membershipTiers.map((tier) => ({
                    id: tier.id,
                    label: tier.name,
                }))}
                control={control}
                name="tier"
            />
            <OwnedField
                id="upgrade-commitment"
                label="Commitment period"
                kind="select"
                options={defaultCommitmentPeriods.map((period) => ({
                    id: period.id,
                    label: period.name,
                }))}
                control={control}
                name="commitmentPeriod"
            />
            <OwnedField
                id="upgrade-billing"
                label="Billing period"
                kind="select"
                options={BillingPeriodId.options.map((id) => ({
                    id,
                    label: BillingPeriodIdMap[id],
                }))}
                control={control}
                name="billingPeriod"
            />
            <DateField
                id="upgrade-start"
                label="Membership start date"
                date={startDate}
                endMonth={tenYearsEndMonth()}
                onDateChange={(date) => {
                    if (date) setStartDate(date);
                }}
            />
            <DateField
                id="upgrade-renewal"
                label="Membership renewal date"
                date={renewalDate}
                disabled
            />
        </FormShell>
    );
}
